"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken, useTenantData } from "@/hooks/shared";
import { invalidateUserTaskQueries } from "@/lib/user-tasks/invalidate-user-task-queries";
import { createAuthenticatedApi } from "@/lib/utils";
import {
  UserTasksApi,
  type SetUserTaskStatusRequest,
  type UserTaskStatus,
} from "@/openapi/generated";

export function useUserTaskStatusUpdate() {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      taskId,
      status,
    }: {
      taskId: string;
      status: UserTaskStatus;
    }) => {
      if (!accessToken || tenantId == null) {
        throw new Error("Authentication required");
      }
      const service = createAuthenticatedApi(UserTasksApi, accessToken);
      const body: SetUserTaskStatusRequest = { status };
      await service.apiUserTasksIdStatusPut({
        id: taskId,
        setUserTaskStatusRequest: body,
        xTenantID: tenantId,
      });
    },
    onSuccess: async () => {
      await invalidateUserTaskQueries(queryClient, tenantId);
    },
  });

  return {
    updateTaskStatus: mutation.mutate,
    updateTaskStatusAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
  };
}
